import { TypedDocumentNode, gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode } from 'react';
import { To } from 'react-router-dom';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { DateRange } from '@sorare/core/src/components/lobby/DateRange';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { WithFixtureHeaderLayout_so5Fixture } from './__generated__/index.graphql';

const Header = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--intermediate-unit) 0;

  @media ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

type Props = {
  title: ReactNode;
  iconUrl?: Nullable<string>;
  so5Fixture: WithFixtureHeaderLayout_so5Fixture;
  children: ReactNode;
  backTo: To;
  action?: ReactNode;
};

export const WithFixtureHeaderLayout = ({
  title,
  iconUrl,
  so5Fixture,
  children,
  backTo,
  action,
}: Props) => {
  return (
    <Container>
      <ContentWithStickyHeader
        header={
          <Header>
            <Horizontal gap={2}>
              <IconButton
                size="medium"
                color="transparent"
                icon={faTimes}
                to={backTo}
              />
              <Horizontal>
                {!!iconUrl && (
                  <img src={iconUrl} alt="" width="32" height="32" />
                )}
                <Vertical gap={0}>
                  <LabelL as="h1" bold>
                    {title}
                  </LabelL>
                  <LabelM as="p" color="var(--c-nd-600)">
                    <DateRange {...so5Fixture} />
                  </LabelM>
                </Vertical>
              </Horizontal>
            </Horizontal>
            {!!action && <div>{action}</div>}
          </Header>
        }
      >
        {children}
      </ContentWithStickyHeader>
    </Container>
  );
};

WithFixtureHeaderLayout.fragments = {
  so5Fixture: gql`
    fragment WithFixtureHeaderLayout_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
    }
  ` as TypedDocumentNode<WithFixtureHeaderLayout_so5Fixture>,
};
